
import { useState } from 'react';
import Button from '../../../components/base/Button';

interface PaymentOptionsProps {
  workshopData: {
    title: string;
    format: string;
    price: number;
    date: string;
  };
  bookingData: {
    participantName: string;
    participantEmail: string;
    participantPhone?: string;
    organization?: string;
    groupSize?: number;
    message?: string;
  };
  onPaymentSelect: (method: string) => void;
  onClose: () => void;
}

export default function PaymentOptions({ workshopData, bookingData, onPaymentSelect, onClose }: PaymentOptionsProps) {
  const [selectedMethod, setSelectedMethod] = useState('');
  const [showInstallments, setShowInstallments] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const paymentMethods = [
    {
      id: 'card',
      name: 'Credit/Debit Card',
      icon: 'ri-bank-card-line',
      description: 'Visa, MasterCard, American Express',
      processing: 'Instant processing',
      popular: true
    },
    {
      id: 'paypal',
      name: 'PayPal',
      icon: 'ri-paypal-line',
      description: 'Pay with your PayPal account',
      processing: 'Instant processing'
    },
    {
      id: 'bank',
      name: 'Bank Transfer',
      icon: 'ri-bank-line',
      description: 'Direct bank transfer',
      processing: '1-3 business days'
    },
    {
      id: 'installments',
      name: 'Payment Plan',
      icon: 'ri-calendar-check-line',
      description: 'Split into 2-3 payments',
      processing: 'Available for $200+ bookings'
    }
  ];

  const installmentOptions = [
    {
      plan: '2 payments',
      amount: Math.ceil(workshopData.price / 2),
      schedule: 'Today + 30 days'
    },
    {
      plan: '3 payments',
      amount: Math.ceil(workshopData.price / 3),
      schedule: 'Today + 30 days + 60 days'
    }
  ];

  const handleMethodSelect = (methodId: string) => {
    setSelectedMethod(methodId);
    if (methodId === 'installments') {
      setShowInstallments(true);
    } else {
      setShowInstallments(false);
    }
  };

  const handleProceed = async () => {
    if (!selectedMethod) return;
    
    setIsProcessing(true);
    
    try {
      let paymentType = 'full';
      if (selectedMethod === 'installments') {
        // For demo, using 2-payment plan
        paymentType = 'installment_2';
      }

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-workshop-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workshopTitle: workshopData.title,
          workshopPrice: workshopData.price,
          workshopFormat: workshopData.format,
          workshopDate: workshopData.date,
          participantName: bookingData.participantName,
          participantEmail: bookingData.participantEmail,
          participantPhone: bookingData.participantPhone,
          organization: bookingData.organization,
          groupSize: bookingData.groupSize,
          message: bookingData.message,
          paymentType
        }),
      });

      const result = await response.json();
      
      if (result.success && result.checkoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = result.checkoutUrl;
      } else {
        throw new Error(result.error || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Failed to process payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-slate-800">Payment Options</h3>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Order Summary */}
        <div className="bg-slate-50 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-slate-800 mb-3">Order Summary</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-600">{workshopData.title}</span>
              <span className="font-semibold">${workshopData.price}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Format: {workshopData.format}</span>
              <span className="text-slate-500">Date: {workshopData.date}</span>
            </div>
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between text-lg font-bold">
                <span>Total:</span>
                <span className="text-blue-600">${workshopData.price}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="mb-6">
          <h4 className="font-semibold text-slate-800 mb-4">Choose Payment Method</h4>
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => handleMethodSelect(method.id)}
                className={`
                  border rounded-lg p-4 cursor-pointer transition-all
                  ${selectedMethod === method.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}
                  ${method.id === 'installments' && workshopData.price < 200 ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-lg">
                      <i className={`${method.icon} text-xl text-slate-600`}></i>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-slate-800">{method.name}</span>
                        {method.popular && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            Popular
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{method.description}</p>
                      <p className="text-xs text-slate-500">{method.processing}</p>
                    </div>
                  </div>
                  <div className="w-5 h-5 flex items-center justify-center">
                    {selectedMethod === method.id && (
                      <i className="ri-check-line text-blue-600"></i>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Installment Options */}
        {showInstallments && workshopData.price >= 200 && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h5 className="font-semibold text-blue-800 mb-3">Payment Plan Options</h5>
            <div className="space-y-3">
              {installmentOptions.map((option, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-white rounded border">
                  <div>
                    <span className="font-medium text-slate-800">{option.plan}</span>
                    <p className="text-sm text-slate-600">{option.schedule}</p>
                  </div>
                  <span className="font-bold text-blue-600">${option.amount}/payment</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-blue-600 mt-3">
              First payment due today. Subsequent payments will be automatically charged.
            </p>
          </div>
        )}

        {/* Security Notice */}
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <i className="ri-shield-check-line text-green-600 text-xl mt-0.5"></i>
            <div>
              <h5 className="font-semibold text-green-800 mb-1">Secure Payment</h5>
              <p className="text-sm text-green-700">
                All payments are processed securely using industry-standard encryption. 
                Your payment information is never stored on our servers.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <Button
            onClick={handleProceed}
            disabled={!selectedMethod || (selectedMethod === 'installments' && workshopData.price < 200) || isProcessing}
            className="flex-1 bg-blue-800 hover:bg-blue-900 disabled:bg-slate-300 disabled:cursor-not-allowed"
            size="lg"
          >
            <i className="ri-secure-payment-line mr-2"></i>
            {isProcessing ? 'Processing...' : 'Proceed to Payment'}
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            className="border-slate-400 text-slate-600 hover:bg-slate-100"
            disabled={isProcessing}
          >
            Back
          </Button>
        </div>

        {/* Terms */}
        <p className="text-xs text-slate-500 mt-4 text-center">
          By proceeding, you agree to our Terms of Service and Privacy Policy. 
          Full refund available if cancelled 7+ days before the workshop date.
        </p>
      </div>
    </div>
  );
}
